import { useState } from "react";

import { Button, Steps as StepsComponent } from "@components";
import { Drawer } from "@components/Drawer";
import { Radio } from "@components/Radio";

import styles from "@styles/forms-flow/trigger.module.scss";

import { BoxTitles } from "@boxes/BoxTitles";
import { Input } from "@components/Input";
import { DaysMonth } from "./DaysMonth";
import { DaysWeek } from "./DaysWeek";
import { SpecifidDay } from "./SpecificDay";

import { Dates } from "@utils/Dates";
import classNames from "classnames/bind";
const css = classNames.bind(styles);

const Steps = {
  FIRING_CLIMATE: 1,
  CONTEMPLATION: 2,
};

const Options = {
  ALL_DAYS: "ALL_DAYS",
  DAYS_WEEK: "DAYS_WEEK",
  DAYS_MONTH: "DAYS_MONTH",
  SPECIFIC_DAY: "SPECIFIC_DAY",
};

export const TriggerForm = ({ visible, onClose, node, onChange, onSave }) => {
  const [currentStep, setCurrentStep] = useState(Steps.FIRING_CLIMATE);
  const [optionToSetup, setOptionToSetup] = useState(null);
  const [contemplation, setContemplation] = useState(null);
  const [selectedData, setSelectedData] = useState(node?.data?.trigger || {});

  const handleBack = () => {
    if (optionToSetup) {
      setCurrentStep(Steps.FIRING_CLIMATE);
      setOptionToSetup(null);

      return;
    }

    if (currentStep === Steps.CONTEMPLATION)
      return setCurrentStep(Steps.FIRING_CLIMATE);
  };

  const getTitle = () => {
    if (!optionToSetup) return BoxTitles.Trigger;

    const titles = {
      DAYS_WEEK: "Em dias específicos da semana",
      DAYS_MONTH: "Em dias específicos do mês",
      SPECIFIC_DAY: "Em um dia específico",
    };

    return titles[optionToSetup];
  };

  const onChangeConfig = (option, data) => {
    let new_data = {};
    switch (option) {
      case Options.ALL_DAYS:
        new_data = { type: "ALL_DAYS", value: true };
        break;
      case Options.DAYS_WEEK:
        new_data =
          data.weekDays.length > 0
            ? { type: "DAYS_WEEK", value: data.weekDays }
            : {};
        break;
      case Options.DAYS_MONTH:
        new_data =
          data.monthDays.length > 0
            ? { type: "DAYS_MONTH", value: data.monthDays }
            : {};
        break;
      case Options.SPECIFIC_DAY:
        new_data =
          data.date.length > 0
            ? { type: "SPECIFIC_DAY", value: data.date }
            : {};
        break;
    }
    setSelectedData(new_data);
    setOptionToSetup(null);
    onChange &&
      onChange({
        ...node,
        data: {
          ...node?.data,
          trigger: new_data,
        },
      });
  };

  return (
    <Drawer
      visible={visible}
      backButton={() => handleBack()}
      onClose={() => {
        onClose();
        setCurrentStep(Steps.FIRING_CLIMATE);
      }}
      showBackButton={currentStep === Steps.CONTEMPLATION || optionToSetup}
      title={getTitle()}
    >
      {!optionToSetup && (
        <>
          <StepsComponent steps={2} currentStep={currentStep} />

          <div className={styles.hairline} />

          {currentStep === Steps.FIRING_CLIMATE && (
            <div className={styles.triggerForm}>
              <p className={styles.label}>Disparar</p>

              <div className={styles.options}>
                <div
                  className={css({
                    option: true,
                    selected: selectedData?.type === "ALL_DAYS",
                  })}
                  onClick={() => {
                    onChangeConfig(Options.ALL_DAYS, { type: "ALL_DAYS" });
                  }}
                >
                  <div className="d-flex align-center">
                    <Radio
                      family="firing"
                      value="ALL_DAYS"
                      checked={selectedData?.type}
                    />
                    <div className={styles.icon}>
                      <i className="fal fa-calendar-days" />
                    </div>
                    <div className="d-flex flex-column">
                      <p className={styles.type}>Todos os dias</p>
                    </div>
                  </div>
                </div>

                <div
                  className={css({
                    option: true,
                    selected: selectedData?.type === "DAYS_WEEK",
                  })}
                  onClick={() => setOptionToSetup(Options.DAYS_WEEK)}
                >
                  <div className="d-flex align-center">
                    <Radio
                      family="firing"
                      value="DAYS_WEEK"
                      checked={selectedData?.type}
                    />
                    <div className={styles.icon}>
                      <i className="fal fa-calendar-day" />
                    </div>
                    <div className="d-flex flex-column">
                      <p className={styles.type}>
                        Em dias específicos da semana
                      </p>
                      {selectedData.type == Options.DAYS_WEEK &&
                        selectedData.value?.length > 0 && (
                          <span className={styles.values}>Qua e Sex</span>
                        )}
                    </div>
                  </div>
                  <div className={styles.chevron}>
                    <i className="fas fa-chevron-right" />
                  </div>
                </div>

                <div
                  className={css({
                    option: true,
                    selected: selectedData?.type === "DAYS_MONTH",
                  })}
                  onClick={() => setOptionToSetup(Options.DAYS_MONTH)}
                >
                  <div className="d-flex align-center">
                    <Radio
                      family="firing"
                      value="DAYS_MONTH"
                      checked={selectedData?.type}
                    />
                    <div className={styles.icon}>
                      <i className="fal fa-calendar" />
                    </div>
                    <div className="d-flex flex-column">
                      <p className={styles.type}>Em dias específicos do mês</p>
                      {selectedData.type == Options.DAYS_MONTH &&
                        selectedData.value?.length > 0 && (
                          <span className={styles.values}>
                            {selectedData?.value?.join(", ")}
                          </span>
                        )}
                    </div>
                  </div>
                  <div className={styles.chevron}>
                    <i className="fas fa-chevron-right" />
                  </div>
                </div>

                <div
                  className={css({
                    option: true,
                    selected: selectedData?.type === "SPECIFIC_DAY",
                  })}
                  onClick={() => setOptionToSetup(Options.SPECIFIC_DAY)}
                >
                  <div className="d-flex align-center">
                    <Radio
                      family="firing"
                      value="SPECIFIC_DAY"
                      checked={selectedData?.type}
                    />
                    <div className={styles.icon}>
                      <i className="fal fa-calendar-minus" />
                    </div>
                    <div className="d-flex flex-column">
                      <p className={styles.type}>Em um dia específico</p>
                      {selectedData.type == Options.SPECIFIC_DAY &&
                        selectedData?.value && (
                          <span className={styles.values}>
                            {Dates.toDateBRL(selectedData?.value)}
                          </span>
                        )}
                    </div>
                  </div>
                  <div className={styles.chevron}>
                    <i className="fas fa-chevron-right" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === Steps.CONTEMPLATION && (
            <div className={styles.triggerForm}>
              <p className={styles.label}>O usuário pode ser contemplado</p>

              <div className={styles.options}>
                <div
                  className={css({
                    option: true,
                    selected: contemplation === "SEVERAL_TIMES",
                  })}
                  onClick={() => setContemplation("SEVERAL_TIMES")}
                >
                  <div className="d-flex align-center">
                    <Radio
                      family="contemplation"
                      value="SEVERAL_TIMES"
                      checked={contemplation}
                    />
                    <div className={styles.icon}>
                      <i className="fal fa-repeat" />
                    </div>
                    <div className="d-flex flex-column">
                      <p className={styles.type}>Várias vezes</p>
                    </div>
                  </div>
                </div>
              </div>

              {contemplation === "SEVERAL_TIMES" && (
                <>
                  <div className={styles.hairline} />

                  <p className={styles.label}>
                    Defina um intervalo de contemplação
                  </p>
                  <Input design="two" label="Intervalo de contemplação" className="mt-20" />
                </>
              )}

              <div className={styles.options}>
                <div
                  className={css({
                    option: true,
                    selected: contemplation === "ONLY_ONCE",
                  })}
                  onClick={() => setContemplation("ONLY_ONCE")}
                >
                  <div className="d-flex align-center">
                    <Radio
                      family="contemplation"
                      value="ONLY_ONCE"
                      checked={contemplation}
                    />
                    <div className={styles.icon}>
                      <i className="fal fa-circle-1" />
                    </div>
                    <div className="d-flex flex-column">
                      <p className={styles.type}>Uma única vez</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className={styles.footer}>
            {currentStep === Steps.FIRING_CLIMATE && (
              <Button
                onClick={() => setCurrentStep(Steps.CONTEMPLATION)}
                className="w-100"
                disabled={!selectedData.type}
                design="primary"
              >
                Continuar
              </Button>
            )}

            {currentStep === Steps.CONTEMPLATION && (
              <Button
                onClick={() => {
                  onSave({
                    ...node,
                    data: {
                      ...node?.data,
                      trigger: selectedData,
                    },
                  });
                  setCurrentStep(Steps.FIRING_CLIMATE);
                }}
                className="w-100"
                design="primary"
              >
                Salvar
              </Button>
            )}
          </div>
        </>
      )}

      {optionToSetup === Options.DAYS_WEEK && (
        <DaysWeek
          data={selectedData}
          onApply={(data) => onChangeConfig(Options.DAYS_WEEK, data)}
        />
      )}
      {optionToSetup === Options.DAYS_MONTH && (
        <DaysMonth
          data={selectedData}
          onApply={(data) => onChangeConfig(Options.DAYS_MONTH, data)}
        />
      )}
      {optionToSetup === Options.SPECIFIC_DAY && (
        <SpecifidDay
          data={selectedData}
          onApply={(data) => onChangeConfig(Options.SPECIFIC_DAY, data)}
        />
      )}
    </Drawer>
  );
};
